import { useState } from 'react';
import { TOPBAR_VIEWS, type ViewId } from '../../lib/app';
import { useSelectedInstrument } from '../../lib/selection';
import { buildQuickSwitch, findInstrument } from '../../lib/instruments';
import { UserMenu } from './UserMenu';

const NAV: { id: ViewId; label: string }[] = [
  { id: 'market', label: '行情' },
  { id: 'chart', label: '图表' },
  { id: 'work', label: '工作区' },
  { id: 'note', label: '笔记' },
  { id: 'review', label: '复盘' },
];

export function TopBar({
  view,
  onViewChange,
  theme,
  onThemeToggle,
}: {
  view: ViewId;
  onViewChange: (v: ViewId) => void;
  theme: 'dark' | 'light';
  onThemeToggle: () => void;
}) {
  const [q, setQ] = useState('');
  const { instrument, selectOnly } = useSelectedInstrument();

  return (
    <header
      // pt-[env(safe-area-inset-top)]：viewport 声明了 viewport-fit=cover，
      // 刘海/挖孔屏的内容会延伸到状态栏下方。不加这个内边距，首行导航会被
      // 物理遮挡（改前只有底栏处理了 safe-area，顶部一直缺）。
      className="flex min-h-[52px] shrink-0 items-center gap-2 border-b px-2 pt-[env(safe-area-inset-top)] sm:gap-3 sm:px-3"
      style={{ background: 'var(--bg-surface)' }}
    >
      {/* 窄屏隐藏 logo 文字：5 项导航 + 搜索 + 主题 + 用户菜单已占满 */}
      <div className="flex shrink-0 items-center gap-1 pl-1">
        <span
          className="grid h-6 w-6 place-items-center rounded text-[13px] font-bold"
          style={{ background: 'var(--accent)', color: '#fff' }}
          aria-hidden
        >
          股
        </span>
        <span className="hidden text-[14px] font-semibold tracking-tight sm:inline">股记</span>
      </div>

      {/* 全局搜索 —— 三组结果：指数 / 笔记 / 新闻（UI-UI-J 落地） */}
      <div className="relative hidden min-w-0 flex-1 sm:block sm:max-w-[280px]">
        <span
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px]"
          style={{ color: 'var(--text-3)' }}
          aria-hidden
        >
          ⌕
        </span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索指数 / 笔记 / 新闻…"
          aria-label="全局搜索"
          className="h-7 w-full rounded-[6px] border bg-transparent pl-7 pr-2 text-[12px] outline-none transition-colors placeholder:text-[var(--text-3)] focus:border-[var(--accent)]"
          style={{ background: 'var(--bg-inset)' }}
        />
      </div>

      {/* 指数快切 —— 与图表页共享同一选中指数，点击就地切换 */}
      <div
        className="hidden min-w-0 flex-1 items-center gap-1 overflow-x-auto lg:flex"
        role="group"
        aria-label="指数快切"
      >
        {buildQuickSwitch(instrument.code).map((code) => {
          const inst = findInstrument(code);
          if (!inst) return null;
          const active = inst.code === instrument.code;
          return (
            <button
              key={code}
              type="button"
              onClick={() => selectOnly(code)}
              aria-pressed={active}
              title={`${inst.name} ${inst.code}`}
              className="shrink-0 rounded px-2 py-1 text-[12px] transition-colors hover:bg-[var(--bg-hover)]"
              style={{
                background: active ? 'var(--bg-elevated)' : 'transparent',
                color: active ? 'var(--text-1)' : 'var(--text-2)',
              }}
            >
              {inst.name}
            </button>
          );
        })}
      </div>

      {/*
        导航移到顶栏（原先移动端底部另有一条 MobileTabBar，两者重复）。
        5 项在窄屏放不下整行，用横向滚动而非换行 —— 换行会把页头撑成两行、
        吃掉更多竖向空间，而这正是移动端最稀缺的资源。
      */}
      <nav
        className="ml-auto flex min-w-0 items-center gap-0.5 overflow-x-auto sm:gap-1"
        aria-label="主视图切换"
      >
        {NAV.filter((n) => TOPBAR_VIEWS.includes(n.id)).map((n) => {
          const active = view === n.id;
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => onViewChange(n.id)}
              aria-current={active ? 'page' : undefined}
              className="shrink-0 whitespace-nowrap rounded px-2 py-1 text-[12px] font-medium transition-colors"
              style={{
                background: active ? 'var(--bg-elevated)' : 'transparent',
                color: active ? 'var(--text-1)' : 'var(--text-2)',
              }}
            >
              {n.label}
            </button>
          );
        })}
      </nav>

      <button
        type="button"
        onClick={onThemeToggle}
        className="grid h-7 w-7 shrink-0 place-items-center rounded text-[12px] transition-colors hover:bg-[var(--bg-hover)]"
        title={theme === 'dark' ? '切换到浅色主题' : '切换到深色主题'}
        aria-label={theme === 'dark' ? '切换到浅色主题' : '切换到深色主题'}
      >
        {theme === 'dark' ? '☾' : '☀'}
      </button>

      <UserMenu />
    </header>
  );
}